import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Move, Maximize2, Minimize2, TestTube, Palette, Download, Upload, Sun, Moon } from 'lucide-react';
import { ColorSchemeEditor } from '../ColorSchemeEditor/ColorSchemeEditor';
import { ColorScheme } from '../ColorSchemeEditor/types';
import { getCurrentColorScheme, applyColorScheme } from '../../services/themeService';
import { useTheme } from '../../context/ThemeContext';
import { ColorSchemesPage } from './ColorSchemesPage';

// Helper function to apply color scheme with theme type compatibility
const applyThemeColorScheme = (scheme: ColorScheme, themeType: 'light' | 'dark' | 'system') => {
  const effectiveTheme: 'light' | 'dark' = 
    themeType === 'system' 
      ? window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light' 
      : themeType as 'light' | 'dark';
  
  applyColorScheme(scheme, effectiveTheme);
};

// Add global styles for dragging
const dragStyles = `
  .no-select {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
    -webkit-touch-callout: none !important;
    -webkit-tap-highlight-color: transparent !important;
  }
  
  .dragging * {
    pointer-events: none !important;
  }
  
  .theme-editor-dragging {
    cursor: move !important;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('theme-editor-drag-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'theme-editor-drag-styles';
  styleElement.textContent = dragStyles;
  document.head.appendChild(styleElement);
}

// Predefined color schemes data
const PREDEFINED_SCHEMES: { name: string; scheme: ColorScheme }[] = [
  // Scheme data truncated for brevity
];

interface ThemeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (scheme: ColorScheme) => void;
}

export const ThemeEditorModal = ({
  isOpen,
  onClose,
  onSave
}: ThemeEditorModalProps): JSX.Element | null => {
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<'editor' | 'schemes' | 'import'>('editor');
  const [currentScheme, setCurrentScheme] = useState<ColorScheme | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 }); // Center by default
  const [colorSearch, setColorSearch] = useState('');
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState({ width: 800, height: 600 });
  const dragRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeOffset = useRef({ x: 0, y: 0 });
  
  // Reset modal position to ensure it's always visible on screen
  const resetPosition = useCallback(() => {
    // Center the modal on screen with safe margins
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Position the modal in the center of the screen
    const centerX = Math.max(screenWidth / 2 - 400, 50); 
    const centerY = Math.max(screenHeight / 2 - 350, 50);
    
    setPosition({ x: centerX, y: centerY });
  }, []);

  // Load current scheme on mount and reset position
  useEffect(() => {
    if (isOpen) {
      const scheme = getCurrentColorScheme();
      setCurrentScheme(scheme);
      resetPosition();
    }
  }, [isOpen, resetPosition]);

  // Emergency reset if modal goes off screen
  useEffect(() => {
    const handleResize = () => {
      if (
        position.x > window.innerWidth - 100 || 
        position.y > window.innerHeight - 100 ||
        position.x < 0 ||
        position.y < 0
      ) {
        resetPosition();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, resetPosition]);
  
  // Drag functionality with best practices
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Prevent text selection during drag
    e.preventDefault();
    e.stopPropagation();
    
    if (dragRef.current) {
      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y
      };
      setIsDragging(true);
      
      // Add user-select: none to body to prevent text selection globally
      document.body.style.userSelect = 'none';
      (document.body.style as any).webkitUserSelect = 'none';
      (document.body.style as any).mozUserSelect = 'none';
      (document.body.style as any).msUserSelect = 'none';
      document.body.style.cursor = 'move';
      
      // Prevent text selection on the entire document
      document.body.classList.add('no-select');
    }
  }, [position.x, position.y]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      // Prevent default behavior and stop propagation
      e.preventDefault();
      e.stopPropagation();
      
      // Calculate new position based on mouse position minus offset
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      
      // Calculate safe boundaries (keep at least 100px visible on each side)
      const minX = 50;
      const minY = 50;
      const maxX = window.innerWidth - 100;
      const maxY = window.innerHeight - 100;
      
      // Apply constrained position with requestAnimationFrame for smooth movement
      requestAnimationFrame(() => {
        setPosition({
          x: Math.max(minX, Math.min(maxX, newX)),
          y: Math.max(minY, Math.min(maxY, newY))
        });
      });
    }
  }, [isDragging]);
  
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      
      // Restore normal cursor and text selection
      document.body.style.userSelect = '';
      (document.body.style as any).webkitUserSelect = '';
      (document.body.style as any).mozUserSelect = '';
      (document.body.style as any).msUserSelect = '';
      document.body.style.cursor = '';
      document.body.classList.remove('no-select');
    }
  }, [isDragging]);
  
  useEffect(() => {
    if (isDragging) {
      // Add event listeners with passive: false to allow preventDefault
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
      
      // Prevent selection and scrolling while dragging
      const preventDefault = (e: Event) => e.preventDefault();
      document.addEventListener('selectstart', preventDefault);
      document.addEventListener('dragstart', preventDefault);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('selectstart', preventDefault);
        document.removeEventListener('dragstart', preventDefault);
        
        // Ensure body styles are restored even if cleanup is delayed
        document.body.style.userSelect = '';
        (document.body.style as any).webkitUserSelect = '';
        (document.body.style as any).mozUserSelect = '';
        (document.body.style as any).msUserSelect = '';
        document.body.style.cursor = '';
        document.body.classList.remove('no-select');
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle resize mouse events
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    resizeOffset.current = {
      x: e.clientX,
      y: e.clientY
    };
    
    setIsResizing(true);
    
    // Add user-select: none to body to prevent text selection during resize
    document.body.style.userSelect = 'none';
    (document.body.style as any).webkitUserSelect = 'none';
    (document.body.style as any).mozUserSelect = 'none';
    (document.body.style as any).msUserSelect = 'none';
    document.body.classList.add('no-select');
  }, []);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing) {
      e.preventDefault();
      
      // Calculate new dimensions based on mouse movement
      const deltaWidth = e.clientX - resizeOffset.current.x;
      const deltaHeight = e.clientY - resizeOffset.current.y;
      
      // Update reference point for next move
      resizeOffset.current = {
        x: e.clientX,
        y: e.clientY
      };
      
      // Apply new size with constraints for min/max dimensions
      requestAnimationFrame(() => {
        setSize(prevSize => ({
          width: Math.max(600, Math.min(window.innerWidth - 40, prevSize.width + deltaWidth)),
          height: Math.max(500, Math.min(window.innerHeight - 40, prevSize.height + deltaHeight))
        }));
      });
    }
  }, [isResizing]);

  const handleResizeMouseUp = useCallback(() => {
    setIsResizing(false);
    
    // Restore normal cursor and text selection
    document.body.style.userSelect = '';
    (document.body.style as any).webkitUserSelect = '';
    (document.body.style as any).mozUserSelect = '';
    (document.body.style as any).msUserSelect = '';
    document.body.classList.remove('no-select');
  }, []);
  
  useEffect(() => {
    if (isResizing) {
      // Add event listeners with passive: false to allow preventDefault
      document.addEventListener('mousemove', handleResizeMouseMove, { passive: false });
      document.addEventListener('mouseup', handleResizeMouseUp);
      
      // Prevent selection and scrolling while dragging
      const preventDefault = (e: Event) => e.preventDefault();
      document.addEventListener('selectstart', preventDefault);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
        document.removeEventListener('selectstart', preventDefault);
        
        // Ensure body styles are restored even if cleanup is delayed
        document.body.style.userSelect = '';
        (document.body.style as any).webkitUserSelect = '';
        (document.body.style as any).mozUserSelect = '';
        (document.body.style as any).msUserSelect = '';
        document.body.classList.remove('no-select');
      };
    }
  }, [isResizing, handleResizeMouseMove, handleResizeMouseUp]);
  
  const handleTestTheme = () => {
    if (currentScheme) {
      // Apply theme without affecting the global theme context
      applyThemeColorScheme(currentScheme, theme);
    }
  };
  
  const handleSaveScheme = (scheme: ColorScheme) => {
    setCurrentScheme(scheme);
    // Don't apply immediately on save, let user test first
    if (onSave) {
      onSave(scheme);
    }
  };
  
  const handleApplyScheme = (scheme: ColorScheme) => {
    setCurrentScheme(scheme);
    applyThemeColorScheme(scheme, theme);
    if (onSave) {
      onSave(scheme);
    }
  };
  
  // When modal is closed, restore original theme state  
  const handleClose = () => {
    // Restore the original theme state by re-applying current scheme
    if (currentScheme) {
      applyThemeColorScheme(currentScheme, theme);
      if (onSave) {
        onSave(currentScheme);
      }
    }
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Modal positioned absolutely, not blocking other UI elements */}
      <div
        ref={dragRef}        
        className={`fixed z-50 flex flex-col bg-background-paper border border-border rounded-lg shadow-2xl transition-all duration-300 overflow-hidden ${
          isMinimized ? 'w-80 h-16' : ''
        }`}        
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: isMinimized ? '320px' : `${size.width}px`,
          height: isMinimized ? '64px' : `${size.height}px`,
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          minWidth: '600px',
          minHeight: isMinimized ? '64px' : '500px',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div 
          className={`flex items-center justify-between p-4 border-b border-border select-none ${
            isDragging ? 'cursor-move' : 'cursor-move'
          }`}
          onMouseDown={handleMouseDown}
          style={{ 
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          <div className="flex items-center space-x-3">
            <Move className="h-5 w-5 text-foreground-muted" />
            <Palette className="h-6 w-6 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              Theme Editor <span style={{color: 'red', fontWeight: 'bold'}}>[THEME-EDITOR-MODAL-KEYWORD]</span>
            </h2>
            {/* Theme Mode Toggle - Moved here for better UX when testing colors */}
            <div className="flex items-center space-x-1 ml-3 px-2 py-1 border border-border rounded-full">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleTheme();
                }}
                className={`p-1.5 rounded-full transition-colors ${theme === 'light' ? 'bg-primary text-primary-foreground' : 'text-foreground-muted'}`}
                title="Light mode"
              >
                <Sun className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleTheme();
                }}
                className={`p-1.5 rounded-full transition-colors ${theme === 'dark' ? 'bg-primary text-primary-foreground' : 'text-foreground-muted'}`}
                title="Dark mode"
              >
                <Moon className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Test Theme Button */}
            <button
              onClick={handleTestTheme}
              className="flex items-center px-3 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors text-sm"
              title="Apply current changes to preview"
            >
              <TestTube className="h-4 w-4 mr-1" />
              Test Theme
            </button>
            
            {/* Window Controls */}
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 rounded-lg hover:bg-secondary/50 transition-colors"
            >
              {isMinimized ? (
                <Maximize2 className="h-4 w-4 text-foreground-muted" />
              ) : (
                <Minimize2 className="h-4 w-4 text-foreground-muted" />
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-destructive/20 text-foreground-muted hover:text-destructive transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="flex flex-col flex-1 min-h-0"> {/* Changed h-full to flex-1 min-h-0 */}
            {/* Tab Navigation */}
            <div className="flex border-b border-border bg-secondary/10">
              <button
                onClick={() => setActiveTab('editor')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'editor'
                    ? 'border-b-2 border-primary text-primary bg-primary/5'
                    : 'text-foreground-muted hover:text-foreground hover:bg-secondary/20'
                }`}
              >
                🎨 Color Editor
              </button>
              <button
                onClick={() => setActiveTab('schemes')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'schemes'
                    ? 'border-b-2 border-primary text-primary bg-primary/5'
                    : 'text-foreground-muted hover:text-foreground hover:bg-secondary/20'
                }`}
              >
                🎯 Color Schemes
              </button>
              <button
                onClick={() => setActiveTab('import')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'import'
                    ? 'border-b-2 border-primary text-primary bg-primary/5'
                    : 'text-foreground-muted hover:text-foreground hover:bg-secondary/20'
                }`}
              >
                📥 Import/Export
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto min-h-0">
              {activeTab === 'editor' && (
                <div className="p-2">
                  {/* Color Search Input */}
                  <div className="mb-2">
                    <input
                      type="text"
                      placeholder="Search colors by name, label, or value..."
                      value={colorSearch}
                      onChange={(e) => setColorSearch(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
                    />
                  </div>
                  <ColorSchemeEditor
                    currentScheme={currentScheme || undefined}
                    onSave={handleSaveScheme}
                    onCancel={onClose}
                    compact={true}
                    colorSearch={colorSearch}
                  />
                </div>
              )}
              {activeTab === 'schemes' && (
                <div className="p-6">
                  <ColorSchemesPage 
                    schemes={PREDEFINED_SCHEMES}
                    currentScheme={currentScheme}
                    onApplyScheme={handleApplyScheme}
                    onPreviewScheme={(scheme) => {
                      // Only preview without changing current scheme
                      applyColorScheme(scheme, theme);
                    }}
                  />
                </div>
              )}
              {activeTab === 'import' && (
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Import & Export
                      </h3>
                      <p className="text-sm text-foreground-muted">
                        Save your custom themes or import themes from files.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Resize Handle */}
        {!isMinimized && (
          <div 
            className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize z-50"
            onMouseDown={handleResizeMouseDown}
            title="Resize"
            style={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                position: 'absolute', 
                bottom: 0, 
                right: 0,
                opacity: 0.7
              }}
            >
              <path d="M8,8 L15,15 M11,15 L15,11 M4,15 L15,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        )}
      </div>
    </>
  );
};
